// ── Tile palette definitions ───────────────────────────────────────────────────
// Each palette provides tile bg/text colors and optional board color overrides.
// Key 0 is used for "super" tiles (value > 2048).

export type PaletteId = 'classic' | 'lavender' | 'icy';

// Subset of Theme that a palette is allowed to override.
// Classic omits this (inherits CSS vars). Alt palettes define all fields.
export interface BoardOverride {
  pageBg:     string;
  boardBg:    string;
  cellEmpty:  string;
  scoreBg:    string;
  scoreLabel: string;
  scoreText:  string;
}

export interface TilePalette {
  id:     PaletteId;
  swatch: string;           // representative color shown in the switcher dot
  board?: BoardOverride;    // when set, overrides CSS-sourced board colors
  bg:     Record<number, string>;
  text:   Record<number, string>;
}

export const PALETTE_ORDER: PaletteId[] = ['classic', 'lavender', 'icy'];

export const PALETTES: Record<PaletteId, TilePalette> = {

  // ── Classic ────────────────────────────────────────────────────────────────
  // Board colors come from CSS custom properties (no override needed).
  classic: {
    id: 'classic',
    swatch: '#f67c5f',
    bg: {
      0: '#3c3a32',
      2: '#eee4da',  4: '#ede0c8',  8: '#f2b179',   16: '#f59563',
      32: '#f67c5f', 64: '#f65e3b', 128: '#edcf72',  256: '#edcc61',
      512: '#edc850', 1024: '#edc53f', 2048: '#edc22e',
    },
    text: {
      0: '#f9f6f2',
      2: '#776e65',  4: '#776e65',  8: '#f9f6f2',   16: '#f9f6f2',
      32: '#f9f6f2', 64: '#f9f6f2', 128: '#f9f6f2',  256: '#f9f6f2',
      512: '#f9f6f2', 1024: '#f9f6f2', 2048: '#f9f6f2',
    },
  },

  // ── Lavender Mist ──────────────────────────────────────────────────────────
  lavender: {
    id: 'lavender',
    swatch: '#9975de',
    board: {
      pageBg:     '#ffffff',   // clean white so purple tiles pop
      boardBg:    '#9e8dc4',   // medium purple — forms the grid frame & gaps
      cellEmpty:  '#ddd5f0',   // very light lavender for empty cells
      scoreBg:    '#9e8dc4',   // matches board
      scoreLabel: '#cfc5e8',   // light purple for "SCORE / BEST" labels
      scoreText:  '#ffffff',
    },
    bg: {
      0: '#4a307a',
      2: '#f5f2fb',  4: '#eee8fa',  8: '#e4daf8',   16: '#d5c4f5',
      32: '#c3aaef', 64: '#ae8fe7', 128: '#9975de',  256: '#8762d3',
      512: '#7956c6', 1024: '#6d4db8', 2048: '#6245a9',
    },
    text: {
      0: '#ffffff',
      2: '#5f5a73',  4: '#59536f',  8: '#514a67',   16: '#ffffff',
      32: '#ffffff', 64: '#ffffff', 128: '#ffffff',  256: '#ffffff',
      512: '#ffffff', 1024: '#ffffff', 2048: '#ffffff',
    },
  },

  // ── Icy Lavender ───────────────────────────────────────────────────────────
  icy: {
    id: 'icy',
    swatch: '#93a7e3',
    board: {
      pageBg:     '#ffffff',   // white background
      boardBg:    '#8aaac8',   // steel blue — grid frame
      cellEmpty:  '#d4e3f4',   // very light icy blue for empty cells
      scoreBg:    '#8aaac8',   // matches board
      scoreLabel: '#c0d5eb',   // light blue for "SCORE / BEST" labels
      scoreText:  '#ffffff',
    },
    bg: {
      0: '#503d8a',
      2: '#f6f8fc',  4: '#edf1fa',  8: '#e2e9f8',   16: '#c7d4f2',
      32: '#b7c7ee', 64: '#a5b8e9', 128: '#93a7e3',  256: '#8a91d8',
      512: '#837ccc', 1024: '#7669ba', 2048: '#6957a6',
    },
    text: {
      0: '#ffffff',
      2: '#586273',  4: '#526072',  8: '#47586c',   16: '#2f3a52',
      32: '#ffffff', 64: '#ffffff', 128: '#ffffff',  256: '#ffffff',
      512: '#ffffff', 1024: '#ffffff', 2048: '#ffffff',
    },
  },

};
